/**
 * Cloudflare Turnstile, loaded on demand (explicit render). The widget stays
 * invisible unless Cloudflare needs the visitor to click; every token is
 * single-use, so `take()` hands it over and asks for a fresh one.
 */

interface TurnstileApi {
	render(container: HTMLElement, options: Record<string, unknown>): string;
	reset(widgetId: string): void;
	remove(widgetId: string): void;
}

declare global {
	interface Window {
		turnstile?: TurnstileApi;
	}
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
	if (window.turnstile) return Promise.resolve(window.turnstile);
	loading ??= new Promise<TurnstileApi>((resolve, reject) => {
		const script = document.createElement('script');
		script.src = SCRIPT_URL;
		script.async = true;
		script.defer = true;
		script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile no cargó')));
		script.onerror = () => {
			loading = null;
			reject(new Error('Turnstile no cargó'));
		};
		document.head.appendChild(script);
	});
	return loading;
};

export class TurnstileWidget {
	private api: TurnstileApi | null = null;
	private widgetId: string | null = null;
	private token: string | null = null;
	private waiters: Array<(token: string) => void> = [];

	/** Called when a token arrives or expires, so the page can refresh its state. */
	constructor(private readonly onChange: () => void = () => undefined) {}

	async render(container: HTMLElement, siteKey: string): Promise<void> {
		this.api = await loadTurnstile();
		this.widgetId = this.api.render(container, {
			sitekey: siteKey,
			appearance: 'interaction-only',
			language: 'es',
			callback: (token: string) => {
				this.token = token;
				for (const resolve of this.waiters.splice(0)) resolve(token);
				this.onChange();
			},
			'expired-callback': () => {
				this.token = null;
				this.onChange();
			},
			'error-callback': () => {
				this.token = null;
				this.onChange();
			},
		});
	}

	get ready(): boolean {
		return this.token !== null;
	}

	/** The current token (waiting for it if needed), then a new challenge for the next call. */
	async take(timeoutMs = 30_000): Promise<string> {
		const token =
			this.token ??
			(await new Promise<string>((resolve, reject) => {
				const timer = setTimeout(() => reject(new Error('turnstile-timeout')), timeoutMs);
				this.waiters.push((value) => {
					clearTimeout(timer);
					resolve(value);
				});
			}));
		this.token = null;
		if (this.api && this.widgetId) this.api.reset(this.widgetId);
		return token;
	}

	destroy(): void {
		if (this.api && this.widgetId) this.api.remove(this.widgetId);
		this.widgetId = null;
	}
}
